import React, { useMemo } from 'react';
import ReactApexChart from "react-apexcharts";
import useChartColors from 'Components/Common/useChartColors';

const ProjectsOverviewCharts = ({ chartId, series }: any) => {
    const chartColors = useChartColors(chartId);

    // Deep clone series to ensure it's mutable (fixes "Cannot add property group, object is not extensible" error)
    const mutableSeries = useMemo(() => {
        if (!series) return [];
        try {
            // Use structuredClone if available (modern browsers), otherwise fallback to JSON
            if (typeof structuredClone !== "undefined") {
                return structuredClone(series);
            }
            return JSON.parse(JSON.stringify(series));
        } catch (e) {
            // If cloning fails, create a new array with copied items
            return Array.isArray(series)
                ? series.map((item: any) => {
                    if (typeof item === "object" && item !== null) {
                        return { ...item };
                    }
                    return item;
                })
                : series;
        }
    }, [series]);

    var options: any = {
        chart: {
            height: 374,
            type: 'line',
            toolbar: {
                show: false,
            }
        },
        stroke: {
            curve: 'smooth',
            dashArray: [0, 3, 0],
            width: [0, 1, 0],
        },
        fill: {
            opacity: [1, 0.1, 1]
        },
        markers: {
            size: [0, 4, 0],
            strokeWidth: 2,
            hover: {
                size: 4,
            }
        },
        xaxis: {
            categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            axisTicks: {
                show: false
            },
            axisBorder: {
                show: false
            }
        },
        grid: {
            show: true,
            xaxis: {
                lines: {
                    show: true,
                }
            },
            yaxis: {
                lines: {
                    show: false,
                }
            },
            padding: {
                top: 0,
                right: -2,
                bottom: 15,
                left: 10
            },
        },
        legend: {
            show: true,
            horizontalAlign: 'center',
            offsetX: 0,
            offsetY: -5,
            markers: {
                width: 9,
                height: 9,
                radius: 6,
            },
            itemMargin: {
                horizontal: 10,
                vertical: 0
            },
        },
        plotOptions: {
            bar: {
                columnWidth: '30%',
                barHeight: '70%'
            }
        },
        colors: chartColors,
        tooltip: {
            shared: true,
            y: [{
                formatter: function (y: any) {
                    if (typeof y !== "undefined") {
                        return y.toFixed(0);
                    }
                    return y;

                }
            }, {
                formatter: function (y: any) {
                    if (typeof y !== "undefined") {
                        return "$" + y.toFixed(2) + "k";
                    }
                    return y;

                }
            }, {
                formatter: function (y: any) {
                    if (typeof y !== "undefined") {
                        return y.toFixed(0);
                    }
                    return y;

                }
            }]
        }
    };
    return (
        <React.Fragment>
            <ReactApexChart dir="ltr"
                options={options}
                series={mutableSeries}
                id={chartId}
                type="line"
                height="374"

                data-colors='["--vz-primary", "--vz-warning", "--vz-success"]'
                data-colors-minimal='["--vz-primary", "--vz-primary-rgb, 0.1", "--vz-primary-rgb, 0.50"]'
                data-colors-interactive='["--vz-primary", "--vz-info", "--vz-warning"]'
                data-colors-creative='["--vz-secondary", "--vz-warning", "--vz-success"]'
                data-colors-corporate='["--vz-primary", "--vz-secondary", "--vz-danger"]'
                data-colors-galaxy='["--vz-primary", "--vz-primary-rgb, 0.1", "--vz-primary-rgb, 0.50"]'
                data-colors-classic='["--vz-primary", "--vz-secondary", "--vz-warning"]'
                className="apex-charts"
            />
        </React.Fragment>
    );
};

const TeamMembersCharts = ({ seriesData, chartsColor }: any) => {
    // Deep clone and normalize series data for radialBar chart
    const series = useMemo(() => {
        let numericValue: number;
        if (typeof seriesData === "string") {
            numericValue = parseFloat(seriesData) || 0;
        } else if (typeof seriesData === "number") {
            numericValue = seriesData;
        } else if (Array.isArray(seriesData) && seriesData.length > 0) {
            // Extract first value if it's an array
            numericValue =
                typeof seriesData[0] === "number"
                    ? seriesData[0]
                    : parseFloat(seriesData[0]) || 0;
        } else {
            numericValue = 0;
        }
        // Ensure value is between 0 and 100 for radialBar
        numericValue = Math.max(0, Math.min(100, numericValue));
        // Return as array of numbers
        return [numericValue];
    }, [seriesData]);

    const options: any = {
        chart: {
            type: 'radialBar',
            width: 36,
            height: 36,
            sparkline: {
                enabled: !0
            }
        },
        dataLabels: {
            enabled: !1
        },
        plotOptions: {
            radialBar: {
                hollow: {
                    margin: 0,
                    size: '50%'
                },
                track: {
                    margin: 1
                },
                dataLabels: {
                    show: !1
                }
            }
        },
        colors: [chartsColor]
    };
    return (
        <React.Fragment>
            <ReactApexChart dir="ltr"
                options={options}
                series={series}
                type="radialBar"
                height={36}
                width={36}
                className="apex-charts"
            />
        </React.Fragment>
    );
};

const PrjectsStatusCharts = ({ chartId, series }: any) => {
    var donutchartProjectsStatusColors = useChartColors(chartId);

    // Deep clone series to ensure it's mutable
    const mutableSeries = useMemo(() => {
        if (!series) return [];
        try {
            if (typeof structuredClone !== "undefined") {
                return structuredClone(series);
            }
            return JSON.parse(JSON.stringify(series));
        } catch (e) {
            return Array.isArray(series) ? [...series] : series;
        }
    }, [series]);

    var options: any = {
        labels: ["Completed", "In Progress", "Yet to Start", "Cancelled"],
        chart: {
            type: "donut",
            height: 230,
        },
        plotOptions: {
            pie: {
                size: 100,
                offsetX: 0,
                offsetY: 0,
                donut: {
                    size: "90%",
                    labels: {
                        show: false,
                    }
                },
            },
        },
        dataLabels: {
            enabled: false,
        },
        legend: {
            show: false,
        },
        stroke: {
            lineCap: "round",
            width: 0
        },
        colors: donutchartProjectsStatusColors,
    };
    return (
        <React.Fragment>
            <ReactApexChart dir="ltr"
                options={options}
                series={mutableSeries}
                id={chartId}
                type="donut"
                height="230"
                
                data-colors='["--vz-success", "--vz-primary", "--vz-warning", "--vz-danger"]' 
                data-colors-minimal='["--vz-primary", "--vz-primary-rgb, 0.85", "--vz-primary-rgb, 0.70", "--vz-primary-rgb, 0.50"]' 
                data-colors-galaxy='["--vz-primary", "--vz-primary-rgb, 0.85", "--vz-primary-rgb, 0.70", "--vz-primary-rgb, 0.50"]'
                className="apex-charts"
            />
        </React.Fragment>
    );
};

export { ProjectsOverviewCharts, TeamMembersCharts, PrjectsStatusCharts };